/*
 * Copyright (c) 2020.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Nikita Tropin <ntropin@yandex.ru>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */
import {ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {UntypedFormControl} from '@angular/forms';
import {NbCardModule, NbTagModule, NbWindowService} from '@nebular/theme';
import {BaseComponent, Catalog, CatalogWithAncestors} from 'compob2b-libs';
import {Observable} from 'rxjs';
import {debounceTime, distinctUntilChanged, filter, map, switchMap} from 'rxjs/operators';
import {translations} from '../../../../../compob2b-admin/src/environments/translations.ru';
import {CatalogService} from '../../../../../compob2b-admin/src/app/cabinet/pim/catalog/catalog.service';
import {CatalogsLinksModalComponent} from './catalogs-links-modal/catalogs-links-modal.component';
import {NgForOf, NgIf} from '@angular/common'
import {ChooseCatalogInputComponent} from '../choose-catalog-input/choose-catalog-input.component'

@Component({
  selector: 'app-catalogs-links',
  templateUrl: './catalogs-links.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NbCardModule,
    NbTagModule,
    NgForOf,
    NgIf,
    ChooseCatalogInputComponent
  ]
})
export class CatalogsLinksComponent extends BaseComponent implements OnInit {
  @Input() catalogId: number;
  @Input() catalogs: Catalog[];
  @Input() canEdit: boolean = true;

  @Output() removeAdditionalCatalogEvent = new EventEmitter<Catalog>();
  @Output() insertAdditionalCatalogEvent = new EventEmitter<Catalog>();

  catalogs$: Observable<Catalog[]>;
  inputFormControl: UntypedFormControl;

  constructor(protected readonly windowService: NbWindowService,
              protected readonly catalogService: CatalogService
  ) {
    super();
  }

  ngOnInit(): void {
    this.inputFormControl = new UntypedFormControl();
    this.catalogs$ = this.inputFormControl.valueChanges
      .pipe(
        debounceTime(100),
        distinctUntilChanged(),
        filter(term => term !== null),
        map((term: string) => term.length < 2 ? null : term),
        switchMap((term: string) => this.searchCatalogs(term)),
      );
  }

  get excludeCatalogIds(): number[] {
    return this.catalogId ? [this.catalogId] : [];
  }

  removeAdditionalCatalog(catalog: Catalog): void {
    if (catalog) {
      this.confirm(translations.confirmDelete + ' ' + translations.relationship + '?', new Observable(() => {
        this.onRemoveAdditionalCatalogFromModal(catalog);
      }));
    }
  }

  onRemoveAdditionalCatalogFromModal(catalog: CatalogWithAncestors | Catalog): void {
    if (catalog) {
      this.removeAdditionalCatalogEvent.emit(catalog as Catalog);
    }
    this.cdr.detectChanges();
  }

  modal(): void {
    const data = {
      catalogId: this.catalogId,
      catalogs: this.catalogs,
      removeAdditionalCatalogEvent: this.removeAdditionalCatalogEvent,
      insertAdditionalCatalogEvent: this.insertAdditionalCatalogEvent
    };

    this.windowService.open(CatalogsLinksModalComponent, {
      context: data,
      title: translations.selectCatalogs
    });
  }

  searchCatalogs(term: string): Observable<Catalog[]> {
    return this.catalogService.searchCatalogsByHeader(term).pipe(
      map(catalogs => catalogs.filter(catalog => catalog.id !== this.catalogId))
    );
  }

  onCatalogSelected(chosenCatalog: CatalogWithAncestors) {
    const catalog: Catalog = {
      id: chosenCatalog.id,
      header: chosenCatalog.header,
      lastLevel: chosenCatalog.lastLevel,
    } as Catalog;

    this.insertAdditionalCatalogEvent.emit(catalog);
    this.cdr.detectChanges();
  }
}
